// Citation style conversion utilities

import { apiClient } from '../api';
import { getStoredCitationMapping, saveCitationMapping, validateAndCleanCitations } from './documentProperties';

/**
 * Superscript Unicode character map for AMA style
 */
const SUPERSCRIPT_MAP = {
  '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴',
  '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹'
};

/**
 * Convert a number to superscript Unicode string
 * @param {number|string} num - Number to convert
 * @returns {string} - Superscript Unicode string
 */
function toSuperscript(num) {
  return num.toString().split('').map(digit => SUPERSCRIPT_MAP[digit] || digit).join('');
}

/**
 * Convert superscript Unicode string back to regular number
 * @param {string} superscript - Superscript Unicode string
 * @returns {string} - Regular number string
 */
function fromSuperscript(superscript) {
  const reverseMap = Object.fromEntries(
    Object.entries(SUPERSCRIPT_MAP).map(([k, v]) => [v, k])
  );
  return superscript.split('').map(char => reverseMap[char] || char).join('');
}

/**
 * Get citation format for a given number and style
 * @param {number} num - Citation number
 * @param {string} style - Citation style
 * @returns {string} - Formatted citation (e.g., "(1)" for Vancouver)
 */
function getCitationFormat(num, style) {
  switch (style.toLowerCase()) {
    case 'vancouver':
    case 'critical-care':
      return `(${num})`;
    case 'ieee':
      return `[${num}]`;
    case 'ama':
      return `${toSuperscript(num)} `; // AMA uses superscript Unicode with space after single citation
    default:
      // For author-date styles, we can't predict the format
      return null;
  }
}

/**
 * Build combined citation format for multiple citations
 * @param {Array} citations - Array of citation objects with in_text property
 * @param {string} style - Citation style
 * @returns {string} - Combined citation (e.g., "(1, 2)" for Vancouver or "(Smith, 2020; Jones, 2021)" for APA)
 */
function buildCombinedCitation(citations, style) {
  if (citations.length === 1) {
    return citations[0].in_text;
  }
  
  const numberedStyles = ['vancouver', 'ieee', 'ama', 'critical-care'];
  const isNumberedStyle = numberedStyles.includes(style.toLowerCase());
  
  if (isNumberedStyle) {
    const numbers = citations.map(cit => {
      if (style.toLowerCase() === 'ama') {
        const text = cit.in_text.trim();
        const superscriptChars = text.match(/[⁰¹²³⁴⁵⁶⁷⁸⁹]+/);
        if (superscriptChars) {
          return superscriptChars[0];
        }
        return cit.in_text.replace(/[^\d]/g, '');
      } else {
        return cit.in_text.replace(/[^\d]/g, '');
      }
    }).join(',');
    
    switch (style.toLowerCase()) {
      case 'vancouver':
      case 'critical-care':
        return `(${numbers})`;
      case 'ieee':
        return `[${numbers}]`;
      case 'ama':
        return numbers;
      default:
        return numbers;
    }
  } else {
    const parts = citations.map(cit => {
      return cit.in_text.replace(/^\(|\)$/g, '');
    }).join('; ');
    
    return `(${parts})`;
  }
}

/**
 * Find and replace text in Word document
 * @param {string} searchText - Text to find
 * @param {string} replaceText - Text to replace with
 * @param {string} style - Citation style (for AMA superscript formatting)
 */
async function findAndReplaceInWord(searchText, replaceText, style = null) {
  return Word.run(async (context) => {
    let searchPattern = searchText;
    
    if (style && style.toLowerCase() === 'ama') {
      const hasSuperscript = /[⁰¹²³⁴⁵⁶⁷⁸⁹]/.test(searchPattern);
      
      if (hasSuperscript) {
        // Search for both superscript and regular number versions
        const regularPattern = fromSuperscript(searchPattern.trim());
        const superscriptPattern = searchPattern.trim();
        
        const searchResults1 = context.document.body.search(superscriptPattern, { 
          matchCase: true,
          matchWholeWord: false
        });
        const searchResults2 = context.document.body.search(regularPattern, { 
          matchCase: true,
          matchWholeWord: false
        });
        
        context.load(searchResults1, 'items');
        context.load(searchResults2, 'items');
        await context.sync();
        
        const allItems = [...searchResults1.items, ...searchResults2.items];
        const uniqueRanges = new Map();
        
        for (const item of allItems) {
          const range = item.getRange();
          const rangeKey = `${range.start}-${range.end}`;
          if (!uniqueRanges.has(rangeKey)) {
            uniqueRanges.set(rangeKey, item);
          }
        }
        
        const uniqueItems = Array.from(uniqueRanges.values());
        
        for (const item of uniqueItems) {
          item.insertText(replaceText, Word.InsertLocation.replace);
        }
        
        await context.sync();
        return;
      } else {
        // Old text is not in AMA format - search for exact pattern only
        const searchResults = context.document.body.search(searchText, { 
          matchCase: true,
          matchWholeWord: false
        });
        context.load(searchResults, 'items');
        await context.sync();
        
        for (const item of searchResults.items) {
          item.insertText(replaceText, Word.InsertLocation.replace);
        }
        
        await context.sync();
        return;
      }
    }
    
    const searchResults = context.document.body.search(searchPattern, { 
      matchCase: true,
      matchWholeWord: false
    });
    context.load(searchResults, 'items');
    await context.sync();
    
    for (const item of searchResults.items) {
      item.insertText(replaceText, Word.InsertLocation.replace);
    }
    
    await context.sync();
  });
}

/**
 * Find and replace using partial match (for citations too long for direct search)
 * @param {string} searchPattern - Short unique pattern to search for (first part of citation)
 * @param {string} fullOldText - Full text that was inserted
 * @param {string} replaceText - New text to replace with
 */
async function findAndReplacePartial(searchPattern, fullOldText, replaceText) {
  return Word.run(async (context) => {
    const body = context.document.body;
    const searchResults = body.search(searchPattern, { matchCase: false });
    context.load(searchResults, 'items');
    await context.sync();
    
    if (searchResults.items.length > 0) {
      for (const item of searchResults.items) {
        try {
          const paragraph = item.paragraphs.getFirst();
          context.load(paragraph, 'text');
          await context.sync();
          
          const paragraphText = paragraph.text;
          const verifyText = fullOldText.substring(0, Math.min(200, fullOldText.length));
          
          if (paragraphText.includes(verifyText)) {
            const updatedParagraphText = paragraphText.replace(fullOldText, replaceText);
            paragraph.clear();
            paragraph.insertText(updatedParagraphText, Word.InsertLocation.start);
          }
        } catch (error) {
          try {
            item.insertText(replaceText, Word.InsertLocation.replace);
          } catch (fallbackError) {
            // ignore
          }
        }
      }
      await context.sync();
    }
  });
}

/**
 * Replace the entire References section with new bibliography
 * @param {Array<string>} newBibliography - New bibliography entries
 */
async function replaceReferencesSection(newBibliography) {
  return Word.run(async (context) => {
    const body = context.document.body;
    const searchResults = body.search("References", { matchCase: false, matchWholeWord: true });
    context.load(searchResults, 'items');
    await context.sync();
    
    if (searchResults.items.length > 0) {
      const allParas = body.paragraphs;
      context.load(allParas, 'items');
      await context.sync();

      // PERFORMANCE FIX: Batch load all paragraph text properties at once
      for (let i = 0; i < allParas.items.length; i++) {
        context.load(allParas.items[i], 'text');
      }
      await context.sync(); // Single sync for all paragraphs instead of N syncs

      let foundRefs = false;
      let referencesHeading = null;
      let parasToDelete = [];
      const refHeadingRegex = /^References\s*$/;

      for (let i = 0; i < allParas.items.length; i++) {
        const para = allParas.items[i];

        if (refHeadingRegex.test(para.text.trim())) {
          foundRefs = true;
          referencesHeading = para;
          parasToDelete = [];
          continue;
        }

        if (foundRefs) {
          parasToDelete.push(para);
        }
      }
      
      for (const para of parasToDelete) {
        para.delete();
      }
      await context.sync();
      
      if (referencesHeading) {
        let insertPoint = referencesHeading;
        for (const entry of newBibliography) {
          insertPoint = insertPoint.insertParagraph(entry, Word.InsertLocation.after);
        }
        await context.sync();
      }
    }
  });
}

/**
 * Check if we can auto-refresh - now supports ALL style conversions!
 * @param {string} oldStyle
 * @param {string} newStyle
 * @returns {boolean}
 */
function canAutoRefresh(oldStyle, newStyle) {
  return true;
}

/**
 * Refresh all citations in the document with new style
 * @param {string} oldStyle - Previous citation style
 * @param {string} newStyle - New citation style
 * @param {Function} showToast - Toast notification function
 */
export async function refreshAllCitations(oldStyle, newStyle, showToast) {
  try {
    // Remove stale/duplicate citations from the mapping before converting styles.
    const { removed } = await validateAndCleanCitations();
    if (removed > 0) {
      showToast("Info", `Cleaned up ${removed} stale citation(s)`, { intent: "info" });
    }

    const mapping = await getStoredCitationMapping();

    if (!mapping.citations || mapping.citations.length === 0) {
      showToast("Info", "No citations found in document", { intent: "warning" });
      return;
    }

    if (!canAutoRefresh(oldStyle, newStyle)) {
      showToast(
        "Warning",
        `Converting from ${oldStyle} to ${newStyle} requires manual update. Please delete References section and re-insert citations.`,
        { intent: "warning" }
      );
      return;
    }

    const allArticleIds = new Set();
    mapping.citations.forEach(cit => {
      if (cit.article_ids && Array.isArray(cit.article_ids)) {
        cit.article_ids.forEach(id => allArticleIds.add(id));
      } else if (cit.article_id) {
        allArticleIds.add(cit.article_id);
      }
    });
    const articleIds = Array.from(allArticleIds).join(',');

    let oldResponse, newResponse;
    const hasBaseCitations = mapping.base_citations && mapping.base_citations.length > 0;

    if (hasBaseCitations && newStyle.toLowerCase() !== 'chicago') {
      oldResponse = await apiClient.getCitations(oldStyle, articleIds, 'both', 'text');
      newResponse = await apiClient.getCitations(newStyle, articleIds, 'both', 'text');

      if (!oldResponse || !newResponse || !newResponse.citations) {
        throw new Error("Failed to generate citations");
      }
    } else {
      oldResponse = await apiClient.getCitations(oldStyle, articleIds, 'both', 'text');
      newResponse = await apiClient.getCitations(newStyle, articleIds, 'both', 'text');

      if (!oldResponse || !newResponse || !newResponse.citations) {
        throw new Error("Failed to generate citations");
      }
    }

    // Create a map from article_id to citation response
    const oldCitationMap = new Map();
    const newCitationMap = new Map();
    oldResponse.citations.forEach(cit => oldCitationMap.set(cit.article_id, cit));
    newResponse.citations.forEach(cit => newCitationMap.set(cit.article_id, cit));
    
    const groups = {};
    mapping.citations.forEach((cit) => {
      const groupId = cit.group_id || `single_${Date.now()}`;
      if (!groups[groupId]) {
        groups[groupId] = {
          ...cit,
          article_ids: cit.article_ids || (cit.article_id ? [cit.article_id] : []),
          oldCitations: [],
          newCitations: []
        };
      }

      const articleIds = groups[groupId].article_ids;
      articleIds.forEach(articleId => {
        const oldCit = oldCitationMap.get(articleId);
        const newCit = newCitationMap.get(articleId);
        if (oldCit && !groups[groupId].oldCitations.find(c => c.article_id === articleId)) {
          groups[groupId].oldCitations.push(oldCit);
        }
        if (newCit && !groups[groupId].newCitations.find(c => c.article_id === articleId)) {
          groups[groupId].newCitations.push(newCit);
        }
      });
    });

    const citationOrder = await Word.run(async (context) => {
      const body = context.document.body;
      context.load(body, 'text');
      await context.sync();

      const documentText = body.text;

      const textToGroupId = new Map();
      Object.keys(groups).forEach(groupId => {
        const group = groups[groupId];
        if (group.inserted_text) {
          textToGroupId.set(group.inserted_text, groupId);
        }
      });

      const allCitations = [];

      for (const [text, groupId] of textToGroupId) {
        let searchIndex = 0;
        while (true) {
          const position = documentText.indexOf(text, searchIndex);
          if (position === -1) {
            break;
          }
          allCitations.push({ groupId, position, text });
          searchIndex = position + 1;
        }
      }

      allCitations.sort((a, b) => a.position - b.position);

      const uniqueOrderedGroupIds = [];
      const seenGroupIds = new Set();

      for (const { groupId } of allCitations) {
        if (!seenGroupIds.has(groupId)) {
          seenGroupIds.add(groupId);
          uniqueOrderedGroupIds.push(groupId);
        }
      }

      Object.keys(groups).forEach(groupId => {
        if (!seenGroupIds.has(groupId)) {
          uniqueOrderedGroupIds.push(groupId);
        }
      });

      return uniqueOrderedGroupIds;
    });
    
    const sortedGroupIds = Object.keys(groups).sort((a, b) => {
      const groupA = groups[a];
      const groupB = groups[b];
      const lengthA = groupA.inserted_text ? groupA.inserted_text.length : 0;
      const lengthB = groupB.inserted_text ? groupB.inserted_text.length : 0;
      return lengthB - lengthA;
    });
    
    const articleIdToNumber = new Map();
    const numberedStyles = ['vancouver', 'ieee', 'ama', 'critical-care'];
    const isNumberedStyle = numberedStyles.includes(newStyle.toLowerCase());

    if (isNumberedStyle && newResponse.bibliography) {
      const articleIdToCitation = new Map();
      newResponse.citations.forEach(cit => {
        articleIdToCitation.set(cit.article_id, cit);
      });

      const articleIdToBibEntry = new Map();
      for (let i = 0; i < newResponse.citations.length; i++) {
        const citation = newResponse.citations[i];
        const articleId = citation.article_id;
        if (i < newResponse.bibliography.length && !articleIdToBibEntry.has(articleId)) {
          articleIdToBibEntry.set(articleId, newResponse.bibliography[i]);
        }
      }

      let nextNumber = 1;
      const seenArticleIds = new Set();

      for (const groupId of citationOrder) {
        const group = groups[groupId];
        if (group.article_ids && group.article_ids.length > 0) {
          for (const articleId of group.article_ids) {
            if (!seenArticleIds.has(articleId)) {
              seenArticleIds.add(articleId);
              articleIdToNumber.set(articleId, nextNumber);
              nextNumber++;
            }
          }
        }
      }

      const articleIdsArray = articleIds.split(',').map(id => parseInt(id));
      for (const articleId of articleIdsArray) {
        if (!articleIdToNumber.has(articleId)) {
          const firstIndex = articleIdsArray.indexOf(articleId);
          if (firstIndex < newResponse.bibliography.length) {
            const entry = newResponse.bibliography[firstIndex];
            const match = entry.match(/^(\d+)\./);
            if (match) {
              const bibNumber = parseInt(match[1]);
              articleIdToNumber.set(articleId, bibNumber);
            }
          }
        }
      }

    }

    for (const groupId of sortedGroupIds) {
      const group = groups[groupId];
      let oldInsertedText = group.inserted_text;

      if (!oldInsertedText) {
        continue;
      }

      let newText;

      if (isNumberedStyle) {
        const newNumbers = [];
        for (const articleId of group.article_ids) {
          const newNum = articleIdToNumber.get(articleId);
          if (newNum !== undefined) {
            newNumbers.push(newNum);
          } else {
            const idx = newResponse.citations.findIndex(c => c.article_id === articleId);
            if (idx !== -1 && idx < newResponse.bibliography.length) {
              const entry = newResponse.bibliography[idx];
              const match = entry.match(/^(\d+)\./);
              if (match) {
                newNumbers.push(parseInt(match[1]));
              }
            }
          }
        }

        if (newNumbers.length > 0) {
          // Remove duplicates and sort
          const uniqueNumbers = [...new Set(newNumbers)].sort((a, b) => a - b);
          const numbersStr = uniqueNumbers.join(', ');

          if (newStyle.toLowerCase() === 'vancouver' || newStyle.toLowerCase() === 'critical-care') {
            newText = `(${numbersStr})`;
          } else if (newStyle.toLowerCase() === 'ieee') {
            newText = `[${numbersStr}]`;
          } else if (newStyle.toLowerCase() === 'ama') {
            const superscriptNumbers = uniqueNumbers.map(n => toSuperscript(n)).join(',');
            if (uniqueNumbers.length === 1) {
              newText = `${superscriptNumbers} `;
            } else {
              newText = superscriptNumbers;
            }
          }
        } else {
          newText = group.newCitations.length === 1
            ? group.newCitations[0].in_text
            : buildCombinedCitation(group.newCitations, newStyle);
        }
      } else {
        // Author-date style (Chicago, APA, etc.)
        if (group.newCitations.length === 1) {
          newText = group.newCitations[0].in_text;
        } else {
          const parts = group.newCitations.map(g => g.in_text.replace(/^\(|\)$/g, '')).join('; ');
          newText = `(${parts})`;
        }
      }

      if (oldInsertedText.length > 250) {
        const searchPattern = oldInsertedText.substring(0, 100);
        await findAndReplacePartial(searchPattern, oldInsertedText, newText);
      } else {
        await findAndReplaceInWord(oldInsertedText, newText, newStyle);
      }
    }

    // Renumber and reorder bibliography to match document citation order.
    // Works for all styles: numbered (Vancouver/AMA/Critical-Care use "N.",
    // IEEE uses "[N]") and author-date (APA/Chicago/... have no prefix, so
    // we prepend one).
    const renumberBibEntry = (entry, num) => {
      if (/^\[\d+\]/.test(entry)) return entry.replace(/^\[\d+\]/, `[${num}]`);
      if (/^\d+\./.test(entry)) return entry.replace(/^\d+\./, `${num}.`);
      return `${num}. ${entry}`;
    };

    let orderedBibliography = newResponse.bibliography;
    if (newResponse.bibliography) {
      const articleIdToBibEntry = new Map();
      for (let i = 0; i < newResponse.citations.length; i++) {
        const citation = newResponse.citations[i];
        const articleId = citation.article_id;
        if (i < newResponse.bibliography.length && !articleIdToBibEntry.has(articleId)) {
          articleIdToBibEntry.set(articleId, newResponse.bibliography[i]);
        }
      }

      orderedBibliography = [];
      const seenArticleIds = new Set();
      let bibNumber = 1;

      for (const groupId of citationOrder) {
        const group = groups[groupId];
        if (group.article_ids && group.article_ids.length > 0) {
          for (const articleId of group.article_ids) {
            if (!seenArticleIds.has(articleId)) {
              seenArticleIds.add(articleId);
              const bibEntry = articleIdToBibEntry.get(articleId);
              if (bibEntry) {
                orderedBibliography.push(renumberBibEntry(bibEntry, bibNumber));
                bibNumber++;
              }
            }
          }
        }
      }

      for (let i = 0; i < newResponse.bibliography.length; i++) {
        const citation = newResponse.citations[i];
        const articleId = citation.article_id;
        if (!seenArticleIds.has(articleId)) {
          orderedBibliography.push(renumberBibEntry(newResponse.bibliography[i], bibNumber));
          bibNumber++;
        }
      }
    }

    await replaceReferencesSection(orderedBibliography);

    const updatedMapping = [];

    for (const groupId of sortedGroupIds) {
      const group = groups[groupId];
      let newInsertedText;

      if (isNumberedStyle) {
        const newNumbers = [];
        for (const articleId of group.article_ids) {
          const newNum = articleIdToNumber.get(articleId);
          if (newNum !== undefined) {
            newNumbers.push(newNum);
          } else {
            const idx = newResponse.citations.findIndex(c => c.article_id === articleId);
            if (idx !== -1 && idx < newResponse.bibliography.length) {
              const entry = newResponse.bibliography[idx];
              const match = entry.match(/^(\d+)\./);
              if (match) {
                newNumbers.push(parseInt(match[1]));
              }
            }
          }
        }

        if (newNumbers.length > 0) {
          // Remove duplicates and sort
          const uniqueNumbers = [...new Set(newNumbers)].sort((a, b) => a - b);
          const numbersStr = uniqueNumbers.join(', ');

          if (newStyle.toLowerCase() === 'vancouver' || newStyle.toLowerCase() === 'critical-care') {
            newInsertedText = `(${numbersStr})`;
          } else if (newStyle.toLowerCase() === 'ieee') {
            newInsertedText = `[${numbersStr}]`;
          } else if (newStyle.toLowerCase() === 'ama') {
            const superscriptNumbers = uniqueNumbers.map(n => toSuperscript(n)).join(',');
            if (uniqueNumbers.length === 1) {
              newInsertedText = `${superscriptNumbers} `;
            } else {
              newInsertedText = superscriptNumbers;
            }
          }
        } else {
          newInsertedText = group.newCitations.length === 1
            ? group.newCitations[0].in_text
            : buildCombinedCitation(group.newCitations, newStyle);
        }
      } else {
        // Author-date style
        if (group.newCitations.length === 1) {
          newInsertedText = group.newCitations[0].in_text;
        } else {
          const parts = group.newCitations.map(g => g.in_text.replace(/^\(|\)$/g, '')).join('; ');
          newInsertedText = `(${parts})`;
        }
      }

      const numbers = newInsertedText.match(/\d+/g) || [];
      const uniqueNumbers = [...new Set(numbers.map(n => parseInt(n)))];

      updatedMapping.push({
        article_ids: group.article_ids,
        count: uniqueNumbers.length,
        group_id: group.group_id,
        inserted_text: newInsertedText
      });
    }

    const baseCitations = mapping.base_citations || null;
    await saveCitationMapping(updatedMapping, newStyle, baseCitations);

  } catch (error) {
    showToast("Error", `Failed to refresh citations: ${error.message}`, { intent: "error" });
    throw error;
  }
}

